// Staged triage decisions live in this browser until Shaun sends them (P12).
// localStorage can throw or come back empty (private window, cleared data); every access is guarded,
// and the page works without it — staged items then last until the tab closes.

import type { Staged } from "./triage";

export const STAGED_KEY = "triage.staged.v1";
export const SENT_KEY = "triage.sent.v1";

export interface StoredState {
  version: 1;
  items: Record<string, Staged>;
  /** Kept across a failed send so a retry reuses it and bee does not save the batch twice. */
  clientBatchId?: string;
}

/** Ids sent from this browser, until bee shows them as pending. Dropped after 1 day. */
export interface SentState {
  version: 1;
  ids: Record<string, string>; // taskId → sentAt ISO
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function loadStaged(): StoredState {
  const s = read<StoredState>(STAGED_KEY);
  if (!s || s.version !== 1 || typeof s.items !== "object" || s.items === null) {
    return { version: 1, items: {} };
  }
  return s;
}

export function saveStaged(state: StoredState): boolean {
  return write(STAGED_KEY, state);
}

export function loadSent(now = Date.now()): SentState {
  const s = read<SentState>(SENT_KEY);
  if (!s || s.version !== 1 || typeof s.ids !== "object" || s.ids === null) {
    return { version: 1, ids: {} };
  }
  const dayAgo = now - 86_400_000;
  const ids: Record<string, string> = {};
  for (const [id, at] of Object.entries(s.ids)) if (Date.parse(at) > dayAgo) ids[id] = at;
  return { version: 1, ids };
}

export function saveSent(state: SentState): boolean {
  return write(SENT_KEY, state);
}

export function newClientBatchId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
